import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { ProgramsFilterInterface, ProgramsFilterModel } from '../../../../models/webinar-course.model';
import { ErrorService } from '../../../../services/error.service';
import { filter, finalize, forkJoin, Observable, switchMap, tap } from 'rxjs';
import { HotTopicModel, RecentSearchInterface } from '../../../../models/global-search.model';
import { GlobalSearchService } from '../../../../services/api/global-search.service';
import { ApiResponse } from '../../../../models/api-response.model';
import { TranslateService } from '@ngx-translate/core';
import { BlockUiService } from '../block-ui/block-ui.service';
import { DialogService } from '../../../../services/dialog.service';

@Component({
    selector: 'app-general-search-dropdown',
    templateUrl: './general-search-dropdown.component.html',
    styleUrl: './general-search-dropdown.component.scss',
})
export class GeneralSearchDropdownComponent implements OnInit {
    @Output() suggestedKeywordClicked = new EventEmitter<string>();
    @Output() filterClearClicked = new EventEmitter<void>();
    @Output() hotTopicClicked = new EventEmitter<void>();

    protected isLoading = false;
    protected hotTopic!: HotTopicModel[];
    protected filterParam = new ProgramsFilterModel({} as ProgramsFilterInterface);
    protected recentSearch: RecentSearchInterface[] = [];

    constructor(
        private errorService: ErrorService,
        private globalSearchService: GlobalSearchService,
        private dialogService: DialogService,
        private translateService: TranslateService,
        private blockUiService: BlockUiService,
    ) {}

    ngOnInit(): void {
        this.initData();
    }

    protected onSuggestedKeyword(keyword: string): void {
        this.suggestedKeywordClicked.emit(keyword);
    }

    protected onFilterClear(): void {
        this.filterClearClicked.emit();

        this.dialogService
            .showConfirmationPopup({
                title: this.translateService.instant('message.deleteSearchHistory'),
                cancelText: 'button.cancel',
                confirmText: 'button.delete',
                icon: 'bg-exclamation-icon !h-[6.25rem] !w-[6.25rem]',
                titleClass: 'text-[1.5rem]',
                isDelete: true,
            })
            .pipe(
                filter((isConfirm) => isConfirm),
                switchMap(() => this.deleteAllRecentSearch$()),
                tap(() => {
                    this.blockUiService.open();
                }),
                finalize(() => this.blockUiService.close()),
            )
            .subscribe();
    }

    protected onClickDelete(id: number): void {
        this.globalSearchService
            .deleteRecentSearch(id)
            .pipe(
                switchMap(() =>
                    this.getRecentSearch$().pipe(
                        tap({
                            next: (response) => {
                                this.recentSearch = response.result;
                            },
                        }),
                    ),
                ),
            )
            .subscribe({
                error: (err) => {
                    this.errorService.showError(err);
                },
            });
    }

    protected onClickHotTopic(): void {
        this.hotTopicClicked.emit();
    }

    private initData(): void {
        this.isLoading = true;

        forkJoin([this.getHotTopic$(), this.getRecentSearch$()])
            .pipe(
                finalize(() => {
                    this.isLoading = false;
                }),
            )
            .subscribe({
                next: ([hotTopicListing, recentSearchListing]) => {
                    this.hotTopic = hotTopicListing.result;
                    this.recentSearch = recentSearchListing.result;
                },
            });
    }

    private getHotTopic$(): Observable<ApiResponse<HotTopicModel[]>> {
        return this.globalSearchService.getHotTopicList().pipe(
            tap({
                error: (error) => {
                    this.errorService.showError(error);
                },
            }),
        );
    }

    private getRecentSearch$(): Observable<ApiResponse<RecentSearchInterface[]>> {
        return this.globalSearchService.getRecentSearch().pipe(
            tap({
                error: (error) => {
                    this.errorService.showError(error);
                },
            }),
        );
    }

    private deleteAllRecentSearch$(): Observable<ApiResponse<boolean>> {
        return this.globalSearchService.deleteAllRecentSearch().pipe(
            tap({
                error: (error) => this.errorService.showError(error),
            }),
        );
    }
}
